// Meta Pixel + Conversions API dual tracking.
//
// Fires a standard event on the browser Pixel AND mirrors it server-side via the
// Conversions API, sharing an event_id so Meta deduplicates the two. This keeps
// conversions measurable even when the browser Pixel is blocked (ad blockers, iOS).

import { sendConversionEvent } from "api";

const META_PIXEL_ID = "2212916949498902";

const ALLOWED_EVENTS = [
  "Contact",
  "Purchase",
  "CompleteRegistration",
  "ViewContent",
  "InitiateCheckout",
];

// localStorage keys for durable, first-party identifiers.
const KEY_EXTERNAL_ID = "_sbh_xid";
const KEY_FBC = "_sbh_fbc";
const KEY_FBP = "_sbh_fbp";
const KEY_USER = "_sbh_ud";

function safeLocalStorage() {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

function lsGet(key) {
  const store = safeLocalStorage();
  try {
    return store ? store.getItem(key) : null;
  } catch (e) {
    return null;
  }
}

function lsSet(key, value) {
  const store = safeLocalStorage();
  try {
    if (store) store.setItem(key, value);
  } catch (e) {
    /* ignore write failures (private mode, quota) */
  }
}

function uuid() {
  if (typeof window !== "undefined" && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Stable first-party visitor id — the single biggest match-quality lever for
// anonymous events. Persisted across sessions (falls back to an in-memory id).
let cachedExternalId = null;
function getExternalId() {
  if (cachedExternalId) return cachedExternalId;
  let id = lsGet(KEY_EXTERNAL_ID);
  if (!id) {
    id = uuid();
    lsSet(KEY_EXTERNAL_ID, id);
  }
  cachedExternalId = id;
  return id;
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// Facebook browser id — prefer the live cookie, persist it, fall back to storage.
function getFbp() {
  const cookie = readCookie("_fbp");
  if (cookie) {
    lsSet(KEY_FBP, cookie);
    return cookie;
  }
  return lsGet(KEY_FBP);
}

// Facebook click id — prefer the _fbc cookie, else build it from a ?fbclid= param
// on the landing URL, then persist so later funnel steps keep the attribution.
function getFbc() {
  const cookie = readCookie("_fbc");
  if (cookie) {
    lsSet(KEY_FBC, cookie);
    return cookie;
  }
  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  if (fbclid) {
    const value = `fb.1.${Date.now()}.${fbclid}`;
    lsSet(KEY_FBC, value);
    return value;
  }
  return lsGet(KEY_FBC);
}

function getStoredUser() {
  try {
    return JSON.parse(lsGet(KEY_USER) || "{}");
  } catch (e) {
    return {};
  }
}

// Remember PII the moment a visitor provides it (contact/booking forms) so that
// later events — especially Purchase on the payment page — can still be matched.
export function rememberUserData({ email, phone, name } = {}) {
  const next = getStoredUser();
  if (email) next.email = email;
  if (phone) next.phone = phone;
  if (name) next.name = name;
  lsSet(KEY_USER, JSON.stringify(next));
}

function splitName(name) {
  if (!name) return {};
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  return { firstName: parts[0], lastName: parts.length > 1 ? parts[parts.length - 1] : undefined };
}

// Push advanced matching (external_id + any known PII) onto the browser Pixel so
// its own events carry match keys too. Re-inits only when the data changes.
let lastAmKey = "";
function applyPixelAdvancedMatching(am) {
  if (typeof window.fbq !== "function") return;
  const key = JSON.stringify(am);
  if (key === lastAmKey) return;
  lastAmKey = key;
  window.fbq("init", META_PIXEL_ID, am);
}

/**
 * Track a standard Meta event on both the browser Pixel and the Conversions API,
 * enriched with a persistent external_id and any known/stored PII so Meta can
 * match it to a person (raises Event Match Quality).
 *
 * @param {string} eventName - one of ALLOWED_EVENTS
 * @param {object} [opts]
 * @param {string} [opts.email] - raw email (hashed server-side)
 * @param {string} [opts.phone] - raw phone incl. country code (hashed server-side)
 * @param {string} [opts.name] - full name (split into fn/ln, hashed server-side)
 * @param {string} [opts.currency] - ISO currency, e.g. "USD" (Purchase)
 * @param {number|string} [opts.value] - conversion value (Purchase)
 * @returns {string|undefined} the shared event id, or undefined if not tracked
 */
export function trackMetaEvent(eventName, opts = {}) {
  if (typeof window === "undefined") return undefined;
  if (!ALLOWED_EVENTS.includes(eventName)) return undefined;

  // Persist any freshly-provided PII, then merge with anything stored earlier.
  if (opts.email || opts.phone || opts.name) rememberUserData(opts);
  const stored = getStoredUser();
  const email = opts.email || stored.email || undefined;
  const phone = opts.phone || stored.phone || undefined;
  const name = opts.name || stored.name || undefined;
  const { firstName, lastName } = splitName(name);

  const { currency, value } = opts;
  const hasValue = value !== undefined && value !== null && value !== "";
  const externalId = getExternalId();
  const eventId = uuid();
  const fbp = getFbp();
  const fbc = getFbc();

  const customData = {};
  if (currency) customData.currency = currency;
  if (hasValue) customData.value = typeof value === "number" ? value : String(value);

  // 1) Browser Pixel — advanced matching + dedup via a shared eventID.
  if (typeof window.fbq === "function") {
    const am = { external_id: externalId };
    if (email) am.em = email;
    if (phone) am.ph = phone;
    if (firstName) am.fn = firstName;
    if (lastName) am.ln = lastName;
    applyPixelAdvancedMatching(am);
    window.fbq("track", eventName, customData, { eventID: eventId });
  }

  // 2) Conversions API (server-side, fire-and-forget).
  sendConversionEvent({
    eventName,
    eventId,
    eventSourceUrl: window.location.href,
    eventTime: Math.floor(Date.now() / 1000),
    externalId,
    email: email || undefined,
    phone: phone || undefined,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    currency: currency || undefined,
    value: hasValue ? value : undefined,
    fbp: fbp || undefined,
    fbc: fbc || undefined,
  }).catch(() => {});

  return eventId;
}

export default trackMetaEvent;
